import React, { useEffect, useState } from 'react';
import { departmentApi } from '@/features/workspace/api';
import { CheckIcon } from './DepartmentIcons';
import { useToast } from '@/hooks/useToast';

export const DepartmentTasksView: React.FC = () => {
  const { notify } = useToast();
  const [tasks, setTasks] = useState<any[]>([]);
  const [, setLoading] = useState<boolean>(true);
  const [title, setTitle] = useState<string>('');
  const [recipient, setRecipient] = useState<string>('Эдвайзер');
  const [deadline, setDeadline] = useState<string>('2026-08-25');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const res = await departmentApi.tasks();
      setTasks(res);
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      setSubmitting(true);
      await departmentApi.createTask({
        title: title.trim(),
        recipient,
        deadline,
        priority: 'HIGH',
      });
      setTitle('');
      setToastMessage('Поручение создано');
      setTimeout(() => setToastMessage(null), 3500);
      fetchTasks();
    } catch {
      notify('Ошибка при создании поручения', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCycleStatus = async (taskId: string) => {
    try {
      const updated = await departmentApi.cycleTaskStatus(taskId);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
    } catch {
      notify('Не удалось обновить статус поручения', 'error');
    }
  };

  const totalTasks = tasks.length;
  const inProgress = tasks.filter((t) => t.status === 'IN_PROGRESS').length;
  const waiting = tasks.filter((t) => t.status === 'WAITING').length;
  const newTasks = tasks.filter((t) => t.status === 'NEW').length;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-6">
        <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
          Task Control
        </span>
        <h2 className="text-2xl font-bold text-white tracking-tight mt-1">
          Поручения
        </h2>
        <p className="text-xs sm:text-sm text-slate-400 mt-1 max-w-4xl">
          Поставьте служебную задачу, назначьте получателя и срок. Статус остаётся в кафедральном контуре.
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">Всего</span>
            <div className="text-3xl font-extrabold text-white mt-1">{totalTasks}</div>
          </div>
          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">В работе</span>
            <div className="text-3xl font-extrabold text-cyan-400 mt-1">{inProgress}</div>
          </div>
          <div className="p-4 rounded-xl border border-amber-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">Ожидают</span>
            <div className="text-3xl font-extrabold text-amber-400 mt-1">{waiting}</div>
          </div>
          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">Новые</span>
            <div className="text-3xl font-extrabold text-white mt-1">{newTasks}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-5">
          <h3 className="text-sm font-bold text-white">Новое поручение</h3>
          <p className="text-xs text-slate-400 mt-0.5">Для Эдвайзера, докторантов или сотрудников.</p>

          <form onSubmit={handleCreateTask} className="mt-4 space-y-4">
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">
                Наименование
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Например: список докторантов 1 курса"
                className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                required
              />
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">
                Получатель
              </label>
              <select
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-400"
              >
                <option value="Эдвайзер">Эдвайзер</option>
                <option value="Докторанты 1–3 курса">Докторанты 1–3 курса</option>
                <option value="Сотрудники кафедры">Сотрудники кафедры</option>
                <option value="Лаборант кафедры">Лаборант кафедры</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">
                Срок
              </label>
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                required
              />
            </div>

            <button
              type="submit"
              disabled={submitting || !title.trim()}
              className="w-full py-2.5 px-4 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold text-xs transition-all shadow active:scale-95 disabled:opacity-50"
            >
              {submitting ? 'Создание...' : 'Создать поручение'}
            </button>
          </form>
        </div>

        <div className="lg:col-span-7 rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-5">
          <h3 className="text-sm font-bold text-white">Активные поручения</h3>
          <p className="text-xs text-slate-400 mt-0.5">Статус можно менять прямо из списка.</p>

          <div className="mt-4 space-y-3">
            {tasks.map((task) => (
              <div
                key={task.id}
                className="p-4 rounded-xl bg-[#061420] border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              >
                <div className="min-w-0 pr-2">
                  <h4 className="text-xs font-bold text-white truncate">{task.title}</h4>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    {task.task_code} · {task.recipient} · срок {task.deadline}
                  </p>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <span
                    className={`text-[10px] font-mono px-2.5 py-1 rounded font-bold ${
                      task.status === 'IN_PROGRESS'
                        ? 'bg-cyan-950 text-cyan-300 border border-cyan-800'
                        : task.status === 'WAITING'
                        ? 'bg-amber-950 text-amber-300 border border-amber-800'
                        : task.status === 'DONE'
                        ? 'bg-emerald-950 text-emerald-300 border border-emerald-800'
                        : 'bg-slate-800 text-slate-300 border border-slate-700'
                    }`}
                  >
                    {task.status}
                  </span>

                  <button
                    type="button"
                    onClick={() => handleCycleStatus(task.id)}
                    className="px-3 py-1 rounded-lg border border-cyan-500/30 bg-[#0c2336] hover:bg-[#112f48] text-cyan-300 text-xs font-semibold transition-all active:scale-95"
                  >
                    Следующий статус
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl bg-[#081a27] border border-cyan-500/40 text-cyan-300 text-xs font-semibold shadow-2xl animate-fade-in">
          <CheckIcon className="w-4 h-4 text-cyan-400" />
          <span>{toastMessage}</span>
        </div>
      )}
    </div>
  );
};
